import React, { useEffect } from "react";
import { Menu, Box } from "@mantine/core";
import MenuFirstStep from "./MenuFirstStep";
import { useFiltersContextHelper } from './useFiltersContextHelper';
import {useIdeaFilters} from "../../../hooks/useIdeaFilters";

export interface ValueLabelPair {
  value: string;
  label: string;
}

export interface FilterBadge {
  text: string;
  color: string; // Mantine color name (e.g., 'blue', 'red', 'green', 'orange', 'purple', etc.)
  hideAt?: string; // ISO date string - badge will be hidden after this date
}

export interface Filter {
  [key: string]: {
    name: string;
    parent: string;
    type: string;
    options: any[];
    formatter?: string;
    operations: ValueLabelPair[];
    ChildFilters?: Filter;
    filterMenuType: "idea" | "evidence";
    badge?: FilterBadge;
  };
}

export interface ActiveFilter {
  operator?: string;
  parent: string;
  query?: string;
  selected?: any[];
  type: string;
  name?: string;
  
}

interface FiltersMenuProps {
  activeFilters: ActiveFilter[];
  setActiveFilters: (obj: any) => void;
  componentType: 'ideas-table' | 'idea-page' | 'discovery';
  Target?: React.ReactElement;
  MenuType: "idea" | "evidence";
  editMode?: boolean;
}

const FiltersMenu: React.FC<FiltersMenuProps> = ({
  componentType,
  MenuType,
  activeFilters,
  Target,
  editMode
}) => {

  // Use helper to get the appropriate context (Customer360 or regular)
  const filtersContext = useFiltersContextHelper();

  const {
    setSelectedParentFilter,
    setChildFiltersValue,
    setNumberValue,
    setRangeDates,
    setOperator,
  } = filtersContext;
  const { ideasFiltersConfig } = useIdeaFilters();

  

  useEffect(() => {
    const unwantedTypes = ["number", "date"];
    const filteredActiveFilters = activeFilters.filter(
      (f) => !unwantedTypes.includes(f.type)
    );

    const initialState = filteredActiveFilters.reduce<{ [key: string]: any[] }>(
      (acc: any, f) => {
        if (f?.selected && Array.isArray(f.selected)) {
          acc[f.parent] = f?.selected?.map((i: any) => typeof i === 'string' ? i.toLowerCase() : i);
        } else {
          acc[f.parent] = [];
        }
        return acc;
      },
      {}
    );

    setChildFiltersValue(initialState);

    const numberFilters = activeFilters.filter((f) => f.type === "number");

    const initialValue = numberFilters.reduce<{ [parent: string]: number }>(
      (acc: any, f) => {
        acc[f.parent] = f.query; // Assign the `query` value to the `parent`.
        return acc;
      },
      {}
    );

    setNumberValue(Object.keys(initialValue).length ? initialValue : undefined);

    const initialOperator = numberFilters.reduce<{ [parent: string]: string }>(
      (acc: Record<string, string>, f) => {
        if (f.parent && f.operator) {
          acc[f.parent] = f.operator;
        }
        return acc;
      },
      {}
    );

    setOperator(Object.keys(initialOperator).length ? initialOperator : undefined);

    const dateFilters = activeFilters.filter((f) => f.type === "date");

    const DateInitialValue = dateFilters.reduce<{
      [parent: string]: [Date | null, Date | null];
    }>((acc: any, f: any) => {
      acc[f.parent] = [f.selected[0], f.selected[1]]; // Convert the strings to Date objects.
      return acc;
    }, {});

    setRangeDates(
      Object.keys(DateInitialValue).length ? DateInitialValue : undefined
    );
 
  }, []);

  const handleParentFilterClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    const name = event.currentTarget.name;
    setSelectedParentFilter(name);
  };

  const { openedMenuType, setOpenedMenuType } = filtersContext;
  
  return (
    <>
      <Menu
        shadow="md"
        width={260}
        position="bottom-start"
        radius="md"
        opened={openedMenuType == MenuType}
        onOpen={() => setOpenedMenuType(MenuType)}
        onClose={() => setOpenedMenuType(null)}
      >
        <Menu.Target>{Target}</Menu.Target>

        <Menu.Dropdown sx={{ padding: 0 }}>
          <Box>
            <MenuFirstStep
              initialfilters={ideasFiltersConfig[MenuType]}
              MenuType={MenuType}
              handleParentFilterClick={handleParentFilterClick}
              componentType={componentType}
              editMode={editMode}
            />
          </Box>
        </Menu.Dropdown>
      </Menu>
    </>
  );
};

export default FiltersMenu;
